import React from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import Search from "@material-ui/icons/Search";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import FarApi from "../../../../assets/js/utils/far/farApi";
import farPageStyle from "../../../../assets/jss/user/far/farPageStyle.jsx";
import { FarPill, FarNotice, formatFarDate } from "./farUi";
import LoaderComponent from "../../Loader";

const useStyles = makeStyles(theme => farPageStyle(theme));

export const FAR_AUDIT_ACTIONS = {
    sent: { label: "Sent", tone: "success" },
    recipient_removed: { label: "Recipient removed", tone: "info" },
    exception_authorized: { label: "Exception authorized", tone: "warning" },
    ignored: { label: "Ignored / acknowledged", tone: "warning" },
    blocked: { label: "Blocked", tone: "danger" }
};

const RESULT_TONE = { authorized: "success", unauthorized: "danger", no_match: "neutral", excluded: "danger" };
const RESULT_LABEL = { authorized: "Authorized", unauthorized: "Not authorized", no_match: "No rule match", excluded: "Excluded (blocked)" };
const EXCLUSION_ACTION_LABEL = { block: "Hard block", admin_approval: "Admin approval", global_policy: "Global policy" };

function FarAuditLogTab(){
    const classes = useStyles();
    const [search, setSearch] = React.useState("");
    const [query, setQuery] = React.useState("");
    const [action, setAction] = React.useState("");
    const [result, setResult] = React.useState("");
    const [page, setPage] = React.useState(1);
    const [data, setData] = React.useState({ items: [], totalItemCount: 0, totalPages: 1 });
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const limit = 25;

    // Debounce the search box.
    React.useEffect(() => {
        const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const load = React.useCallback((source) => {
        setLoading(true);
        setError(null);
        const params = { page, limit };
        if(query){ params.search = query; }
        if(action){ params.action = action; }
        if(result){ params.result = result; }
        FarApi.getAuditLog(params, source).then(result => {
            setData(result);
            setLoading(false);
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            setLoading(false);
            setError((err && err.message) || "The audit log could not be loaded.");
        });
    }, [page, query, action, result]);

    React.useEffect(() => {
        const source = axios.CancelToken.source();
        load(source);
        return () => source.cancel("Request Cancelled");
    }, [load]);

    const totalPages = Math.max(1, data.totalPages || 1);

    return (
        <div className={classes.main}>
            <div className={classes.toolbar}>
                <div className={classes.searchWrap}>
                    <Search aria-hidden="true" />
                    <label htmlFor="far-audit-search" className={classes.srOnly}>Search audit log</label>
                    <input id="far-audit-search" type="search" className={classes.search} placeholder="Search by file, rule, recipient, user, reason or transfer…"
                        value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <label htmlFor="far-audit-action" className={classes.srOnly}>Filter by action</label>
                <select id="far-audit-action" className={classes.select} value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
                    <option value="">All Actions</option>
                    { Object.keys(FAR_AUDIT_ACTIONS).map(key => <option key={key} value={key}>{FAR_AUDIT_ACTIONS[key].label}</option>) }
                </select>
                <label htmlFor="far-audit-result" className={classes.srOnly}>Filter by result</label>
                <select id="far-audit-result" className={classes.select} value={result} onChange={(e) => { setResult(e.target.value); setPage(1); }}>
                    <option value="">All Results</option>
                    { Object.keys(RESULT_LABEL).map(key => <option key={key} value={key}>{RESULT_LABEL[key]}</option>) }
                </select>
            </div>
            <div className={classes.tableCard}>
                <div className={classes.tableScroll}>
                    <table className={classes.table}>
                        <caption className={classes.srOnly}>File Attachment Rules audit log</caption>
                        <thead>
                            <tr>
                                <th scope="col">Date &amp; Time</th>
                                <th scope="col">User</th>
                                <th scope="col">File</th>
                                <th scope="col">Rule</th>
                                <th scope="col">Recipient</th>
                                <th scope="col">Result</th>
                                <th scope="col">Action</th>
                                <th scope="col">Reason</th>
                                <th scope="col">Transfer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                loading ?
                                    <tr><td colSpan={9} className={classes.empty}><LoaderComponent color="custom" align="center" /></td></tr>
                                : error ?
                                    <tr><td colSpan={9} className={classes.empty}>
                                        <FarNotice tone="danger" action={<button type="button" className={classes.bulkButton} onClick={() => load(null)}>Retry</button>}>{error}</FarNotice>
                                    </td></tr>
                                : data.items.length <= 0 ?
                                    <tr><td colSpan={9} className={classes.empty}>No audit events{ query || action || result ? " match your filters" : " yet" }.</td></tr>
                                :
                                    data.items.map((item, key) => {
                                        const act = FAR_AUDIT_ACTIONS[item.action] || { label: item.action || "—", tone: "neutral" };
                                        return (
                                            <tr key={item.id || key}>
                                                <td className={classes.modified}>{formatFarDate(item.created_at, true)}</td>
                                                <td>
                                                    {item.user_name || "—"}
                                                    { item.user_email ? <p className={classes.ruleDesc}>{item.user_email}</p> : null }
                                                </td>
                                                <td style={{wordBreak: "break-word", minWidth: "160px"}}>
                                                    {item.file_name}
                                                    { item.file_path && item.file_path !== item.file_name ? <p className={classes.ruleDesc}>{item.file_path}</p> : null }
                                                </td>
                                                <td>{item.rule_name || (item.rule_id ? "#"+item.rule_id : "—")}</td>
                                                <td style={{wordBreak: "break-all"}}>
                                                    {item.recipient || "—"}
                                                    { item.field ? <p className={classes.ruleDesc}>{String(item.field).toUpperCase()}</p> : null }
                                                </td>
                                                <td className={classes.resultCell}>
                                                    <FarPill tone={RESULT_TONE[item.result] || "neutral"}>{RESULT_LABEL[item.result] || item.result || "—"}</FarPill>
                                                    {
                                                        item.result === "excluded" ?
                                                            <p className={classes.ruleDesc} style={{whiteSpace: "normal", minWidth: "130px"}}>
                                                                {item.exclusion_type === "domain" ? "Blocked domain" : item.exclusion_type === "email" ? "Blocked address" : "Blocked recipient"}
                                                                { item.exclusion_action ? " · "+(EXCLUSION_ACTION_LABEL[item.exclusion_action] || item.exclusion_action) : "" }
                                                            </p>
                                                        : null
                                                    }
                                                </td>
                                                <td className={classes.resultCell}><FarPill tone={act.tone}>{act.label}</FarPill></td>
                                                <td className={classes.reason}>
                                                    {item.reason || "—"}
                                                    { item.authorized_by_name ? <p className={classes.ruleDesc}>By {item.authorized_by_name}</p> : null }
                                                </td>
                                                <td>{item.transfer_id || "—"}</td>
                                            </tr>
                                        );
                                    })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            <div className={classes.footer}>
                <span role="status">{data.totalItemCount} event{data.totalItemCount === 1 ? "" : "s"}</span>
                <div className={classes.pager}>
                    <button type="button" className={classes.pageButton} aria-label="Previous page" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}><ChevronLeft /></button>
                    <span>Page {page} of {totalPages}</span>
                    <button type="button" className={classes.pageButton} aria-label="Next page" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}><ChevronRight /></button>
                </div>
            </div>
        </div>
    );
}

export default FarAuditLogTab;
